import React, { useEffect, useState } from 'react';
import { Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthProvider';

const Login: React.FC = () => {
  const { user, loading, signInWithGoogle } = useAuth();
  const [error, setError] = useState('');
  const [signInLoading, setSignInLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect to landing page if already authenticated
  useEffect(() => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}][Login] Checking auth state, user:`, user, 'loading:', loading);
    if (!loading && user) {
      console.log(`[${timestamp}][Login] User authenticated, redirecting to /`);
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleSignIn = async () => {
    const timestamp = new Date().toISOString();
    setError('');
    setSignInLoading(true);
    try {
      console.log(`[${timestamp}][Login] Initiating Google Sign-In`);
      await signInWithGoogle();
      console.log(`[${timestamp}][Login] Google Sign-In initiated, awaiting callback`);
    } catch (error: any) {
      console.error(`[${timestamp}][Login] Google Sign-In failed:`, error);
      setError(error?.message || 'Failed to sign in with Google. Please try again.');
      setSignInLoading(false);
    }
  };

  if (loading) {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}][Login] Auth context is loading`);
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <span className="animate-spin inline-block h-8 w-8 border-2 border-white border-t-transparent rounded-full"></span>
        <p className="mt-4 text-white/70">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8 text-white">
          <Globe className="h-12 w-12 mx-auto mb-4" aria-label="TDMA Logo" />
          <h2 className="text-3xl font-bold mb-2">Welcome to TDMA</h2>
          <p className="text-white/70">Sign in to explore the landing page and all website features</p>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-xl p-8 text-white">
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

          <button
            onClick={handleGoogleSignIn}
            disabled={signInLoading}
            className="w-full bg-white/5 border border-white/10 text-white rounded-lg px-4 py-3 font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {signInLoading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                Signing In...
              </span>
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;